import { useCallback, useEffect, useState } from 'react'

import { useBranch } from '@/contexts/BranchContext'
import {
  addSaleAddon,
  addSaleLine,
  applyHeaderDiscount,
  applyLineDiscount,
  applyPromoCode,
  createSale,
  fetchServiceAddons,
  fetchSuspendedSales,
  loadAppointmentToSale,
  paySale as paySaleApi,
  recallSale as recallSaleApi,
  removePromoCode,
  removeSaleAddon,
  suspendSale as suspendSaleApi,
  updateSale,
  updateSaleLine,
  voidSaleLine,
  enqueueSaleToWalkinQueue,
} from '@/lib/api/sales'
import { fetchPosStylists } from '@/lib/api/pos'
import { addSalePackageLine, addSaleRedeemLine, addSaleRedeemLines } from '@/lib/api/packages'
import {
  packagePayErrorMessage,
  packageRedeemErrorMessage,
} from '@/lib/pos/posPackageRedeemErrors'
import { canHoldSale } from '@/components/pos/posHold'
import { getParentSaleLines } from '@/components/pos/posCart'
import { nextLineStatus } from '@/components/pos/posLineStatus'
import { fetchPosPaymentMethods } from '@/lib/api/paymentMethods'

/**
 * @param {object} options
 * @param {import('./posMockData').PosGender} options.gender
 */
export function usePosSale({ initialGender }) {
  const { activeBranchId, activeBranch } = useBranch()
  const [sale, setSale] = useState(null)
  const [stylists, setStylists] = useState([])
  const [paymentMethods, setPaymentMethods] = useState([])
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(true)
  const [addonOptionsByService, setAddonOptionsByService] = useState({})
  const [loading, setLoading] = useState(true)
  const [mutating, setMutating] = useState(false)
  const [error, setError] = useState('')
  const [customerError, setCustomerError] = useState('')
  const [paymentError, setPaymentError] = useState('')
  const [holdError, setHoldError] = useState('')
  const [suspendedSales, setSuspendedSales] = useState([])
  const [loadingSuspended, setLoadingSuspended] = useState(false)
  const [appointmentError, setAppointmentError] = useState('')
  const [packageError, setPackageError] = useState('')
  const [redeemError, setRedeemError] = useState('')
  const [discountError, setDiscountError] = useState('')
  const [floorQueueError, setFloorQueueError] = useState('')

  const loadStylists = useCallback(async () => {
    try {
      const items = await fetchPosStylists()
      setStylists(items)
    } catch {
      setStylists([])
    }
  }, [])

  const loadPaymentMethods = useCallback(async () => {
    setLoadingPaymentMethods(true)
    try {
      const items = await fetchPosPaymentMethods()
      setPaymentMethods(items)
    } catch {
      setPaymentMethods([])
    } finally {
      setLoadingPaymentMethods(false)
    }
  }, [])

  const ensureAddonOptions = useCallback(async (serviceId) => {
    if (!serviceId || addonOptionsByService[serviceId]) {
      return addonOptionsByService[serviceId] ?? []
    }

    try {
      const options = await fetchServiceAddons(serviceId)
      setAddonOptionsByService((current) => ({ ...current, [serviceId]: options }))
      return options
    } catch {
      return []
    }
  }, [addonOptionsByService])

  useEffect(() => {
    if (!activeBranchId) {
      setSale(null)
      setLoading(false)
      return
    }

    let active = true

    async function init() {
      setLoading(true)
      setError('')
      try {
        const draft = await createSale({ gender_price: initialGender })
        await Promise.all([loadStylists(), loadPaymentMethods()])
        if (!active) return
        setSale(draft)
      } catch (err) {
        if (!active) return
        setSale(null)
        setError(err?.response?.data?.message ?? 'Unable to start POS ticket.')
      } finally {
        if (active) setLoading(false)
      }
    }

    init()

    return () => {
      active = false
    }
  }, [activeBranchId, initialGender, loadPaymentMethods, loadStylists])

  const addService = useCallback(async (service) => {
    if (!sale) return null

    setMutating(true)
    setError('')
    try {
      const result = await addSaleLine(sale.id, {
        line_type: 'service',
        service_id: service.id,
      })
      setSale(result.sale)
      await ensureAddonOptions(service.id)
      return result.sale
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Unable to add service to cart.')
      return null
    } finally {
      setMutating(false)
    }
  }, [ensureAddonOptions, sale])

  const addProduct = useCallback(async (product) => {
    if (!sale) return null

    setMutating(true)
    setError('')
    try {
      const result = await addSaleLine(sale.id, {
        line_type: 'product',
        product_id: product.id,
      })
      setSale(result.sale)
      return result.sale
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Unable to add product to cart.')
      return null
    } finally {
      setMutating(false)
    }
  }, [sale])

  const assignStylist = useCallback(async (lineId, stylistId, splitPercent) => {
    if (!sale) return null

    setMutating(true)
    setError('')
    try {
      const result = await updateSaleLine(sale.id, lineId, {
        stylist_id: stylistId,
        stylist_split_percent: stylistId ? splitPercent : null,
      })
      setSale(result.sale)
      return result.sale
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Unable to assign stylist.')
      return null
    } finally {
      setMutating(false)
    }
  }, [sale])

  const changeLineQuantity = useCallback(async (lineId, quantity) => {
    if (!sale) return null

    setMutating(true)
    setError('')
    try {
      const result = await updateSaleLine(sale.id, lineId, { quantity })
      setSale(result.sale)
      return result.sale
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Unable to update line quantity.')
      return null
    } finally {
      setMutating(false)
    }
  }, [sale])

  const attachAddon = useCallback(async (parentLineId, addonServiceId) => {
    if (!sale) return null

    setMutating(true)
    setError('')
    try {
      const result = await addSaleAddon(sale.id, parentLineId, { addon_service_id: addonServiceId })
      setSale(result.sale)
      return result.sale
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Unable to attach add-on.')
      return null
    } finally {
      setMutating(false)
    }
  }, [sale])

  const detachAddon = useCallback(async (addonLineId) => {
    if (!sale) return null

    setMutating(true)
    setError('')
    try {
      const updated = await removeSaleAddon(sale.id, addonLineId)
      setSale(updated)
      return updated
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Unable to remove add-on.')
      return null
    } finally {
      setMutating(false)
    }
  }, [sale])

  const voidLine = useCallback(async (lineId) => {
    if (!sale) return null

    setMutating(true)
    setError('')
    try {
      const updated = await voidSaleLine(sale.id, lineId)
      setSale(updated)
      return updated
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Unable to remove line from ticket.')
      return null
    } finally {
      setMutating(false)
    }
  }, [sale])

  const updateLineDetails = useCallback(async (lineId, payload) => {
    if (!sale) return null

    setMutating(true)
    setError('')
    try {
      const result = await updateSaleLine(sale.id, lineId, payload)
      setSale(result.sale)
      return result.sale
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Unable to update line.')
      throw err
    } finally {
      setMutating(false)
    }
  }, [sale])

  const advanceLineStatus = useCallback(async (lineId) => {
    if (!sale) return null

    const line = getParentSaleLines(sale).find((item) => item.id === lineId)
    if (!line || line.line_type !== 'service') {
      return null
    }

    const current = /** @type {import('./posLineStatus').PosLineStatus} */ (
      line.line_status ?? 'waiting'
    )
    const next = nextLineStatus(current)
    if (next === current) {
      return sale
    }

    return updateLineDetails(lineId, { line_status: next })
  }, [sale, updateLineDetails])

  const enqueueToFloorQueue = useCallback(async () => {
    if (!sale) return null

    setMutating(true)
    setFloorQueueError('')
    try {
      await enqueueSaleToWalkinQueue(sale.id)
      return true
    } catch (err) {
      setFloorQueueError(err?.response?.data?.message ?? 'Unable to send guest to floor queue.')
      return null
    } finally {
      setMutating(false)
    }
  }, [sale])

  const duplicateLine = useCallback(async (line) => {
    if (!sale || !line) return null

    setMutating(true)
    setError('')
    try {
      const payload = line.line_type === 'product'
        ? { line_type: 'product', product_id: line.product_id }
        : { line_type: 'service', service_id: line.service_id }

      if (!payload.product_id && !payload.service_id) {
        return null
      }

      const result = await addSaleLine(sale.id, payload)
      setSale(result.sale)
      if (line.service_id) {
        await ensureAddonOptions(line.service_id)
      }
      return result.sale
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Unable to duplicate line.')
      return null
    } finally {
      setMutating(false)
    }
  }, [ensureAddonOptions, sale])

  const updateSaleNotes = useCallback(async (notes) => {
    if (!sale) return null

    setMutating(true)
    setError('')
    try {
      const updated = await updateSale(sale.id, { notes })
      setSale(updated)
      return updated
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Unable to save ticket note.')
      return null
    } finally {
      setMutating(false)
    }
  }, [sale])

  const applyLineDiscountToSale = useCallback(async (lineId, payload) => {
    if (!sale) return null

    setMutating(true)
    setDiscountError('')
    try {
      const result = await applyLineDiscount(sale.id, lineId, payload)
      setSale(result.sale)
      return result.sale
    } catch (err) {
      setDiscountError(err?.response?.data?.message ?? 'Unable to apply line discount.')
      throw err
    } finally {
      setMutating(false)
    }
  }, [sale])

  const applyHeaderDiscountToSale = useCallback(async (payload) => {
    if (!sale) return null

    setMutating(true)
    setDiscountError('')
    try {
      const updated = await applyHeaderDiscount(sale.id, payload)
      setSale(updated)
      return updated
    } catch (err) {
      setDiscountError(err?.response?.data?.message ?? 'Unable to apply bill discount.')
      throw err
    } finally {
      setMutating(false)
    }
  }, [sale])

  const applyPromoCodeToSale = useCallback(async (payload) => {
    if (!sale) return null

    setMutating(true)
    setDiscountError('')
    try {
      const updated = await applyPromoCode(sale.id, payload)
      setSale(updated)
      return updated
    } catch (err) {
      setDiscountError(err?.response?.data?.message ?? 'Unable to apply voucher.')
      throw err
    } finally {
      setMutating(false)
    }
  }, [sale])

  const removePromoCodeFromSale = useCallback(async () => {
    if (!sale) return null

    setMutating(true)
    setDiscountError('')
    try {
      const updated = await removePromoCode(sale.id)
      setSale(updated)
      return updated
    } catch (err) {
      setDiscountError(err?.response?.data?.message ?? 'Unable to remove voucher.')
      return null
    } finally {
      setMutating(false)
    }
  }, [sale])

  const changeGenderPrice = useCallback(async (nextGender) => {
    if (!sale) return null

    setMutating(true)
    setError('')
    try {
      const updated = await updateSale(sale.id, { gender_price: nextGender })
      setSale(updated)
      return updated
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Unable to update gender pricing.')
      return null
    } finally {
      setMutating(false)
    }
  }, [sale])

  const assignCustomer = useCallback(async (customerId) => {
    if (!sale) return null

    setMutating(true)
    setCustomerError('')
    try {
      const updated = await updateSale(sale.id, { customer_id: customerId })
      setSale(updated)
      return updated
    } catch (err) {
      setCustomerError(err?.response?.data?.message ?? 'Unable to assign guest to ticket.')
      return null
    } finally {
      setMutating(false)
    }
  }, [sale])

  const clearCustomer = useCallback(async () => {
    return assignCustomer(null)
  }, [assignCustomer])

  const paySale = useCallback(async (payload) => {
    if (!sale) return null

    setMutating(true)
    setPaymentError('')
    try {
      const result = await paySaleApi(sale.id, payload)
      setSale(result.sale)
      return { changeDue: result.changeDue, sale: result.sale }
    } catch (err) {
      const message = packagePayErrorMessage(err, 'Unable to complete payment.')
      setPaymentError(message)
      return null
    } finally {
      setMutating(false)
    }
  }, [sale])

  const startNewTicket = useCallback(async () => {
    setMutating(true)
    setError('')
    setPaymentError('')
    setCustomerError('')
    setHoldError('')
    try {
      const gender = sale?.gender_price === 'gents' ? 'gents' : 'ladies'
      const draft = await createSale({ gender_price: gender })
      setSale(draft)
      setAddonOptionsByService({})
      return draft
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Unable to start a new ticket.')
      return null
    } finally {
      setMutating(false)
    }
  }, [sale?.gender_price])

  const loadSuspendedSales = useCallback(async () => {
    setLoadingSuspended(true)
    setHoldError('')
    try {
      const items = await fetchSuspendedSales()
      setSuspendedSales(items)
      return items
    } catch (err) {
      setSuspendedSales([])
      setHoldError(err?.response?.data?.message ?? 'Unable to load held tickets.')
      return []
    } finally {
      setLoadingSuspended(false)
    }
  }, [])

  const holdCurrentSale = useCallback(async () => {
    if (!sale || !canHoldSale(sale)) return null

    setMutating(true)
    setHoldError('')
    try {
      await suspendSaleApi(sale.id)
      const draft = await startNewTicket()
      await loadSuspendedSales()
      return draft
    } catch (err) {
      setHoldError(err?.response?.data?.message ?? 'Unable to hold ticket.')
      return null
    } finally {
      setMutating(false)
    }
  }, [loadSuspendedSales, sale, startNewTicket])

  const recallSuspendedSale = useCallback(async (saleId) => {
    if (!saleId) return null

    setMutating(true)
    setHoldError('')
    try {
      if (sale && sale.id !== saleId && canHoldSale(sale)) {
        await suspendSaleApi(sale.id)
      }

      const recalled = await recallSaleApi(saleId)
      setSale(recalled)
      setAddonOptionsByService({})

      const serviceIds = getParentSaleLines(recalled)
        .map((line) => line.service_id)
        .filter(Boolean)

      await Promise.all(serviceIds.map((serviceId) => ensureAddonOptions(serviceId)))
      await loadSuspendedSales()
      return recalled
    } catch (err) {
      setHoldError(err?.response?.data?.message ?? 'Unable to recall ticket.')
      return null
    } finally {
      setMutating(false)
    }
  }, [ensureAddonOptions, loadSuspendedSales, sale])

  const loadAppointmentOntoTicket = useCallback(async (appointmentId) => {
    if (!sale || !appointmentId) return null

    setMutating(true)
    setAppointmentError('')

    try {
      const result = await loadAppointmentToSale(sale.id, appointmentId)
      setSale(result.sale)

      const serviceIds = getParentSaleLines(result.sale)
        .map((line) => line.service_id)
        .filter(Boolean)

      await Promise.all(serviceIds.map((serviceId) => ensureAddonOptions(serviceId)))

      return result.sale
    } catch (err) {
      setAppointmentError(err?.response?.data?.message ?? 'Unable to load appointment onto ticket.')
      return null
    } finally {
      setMutating(false)
    }
  }, [ensureAddonOptions, sale])

  const sellPackage = useCallback(async (payload) => {
    if (!sale) return null

    setMutating(true)
    setPackageError('')

    try {
      const result = await addSalePackageLine(sale.id, payload)
      setSale(result.sale)
      return result.sale
    } catch (err) {
      const fieldErrors = err?.response?.data?.errors
      const firstFieldError = fieldErrors
        ? Object.values(fieldErrors).flat().find(Boolean)
        : null
      setPackageError(
        firstFieldError
        ?? err?.response?.data?.message
        ?? 'Unable to add package to ticket.',
      )
      return null
    } finally {
      setMutating(false)
    }
  }, [sale])

  const redeemPackageService = useCallback(async (payload) => {
    if (!sale) return null

    setMutating(true)
    setRedeemError('')

    try {
      const result = await addSaleRedeemLine(sale.id, payload)
      setSale(result.sale)
      return result.sale
    } catch (err) {
      setRedeemError(packageRedeemErrorMessage(
        err,
        'Unable to add package redemption to ticket.',
      ))
      return null
    } finally {
      setMutating(false)
    }
  }, [sale])

  const redeemPackageServices = useCallback(async (payload) => {
    if (!sale) return null

    setMutating(true)
    setRedeemError('')

    try {
      const result = await addSaleRedeemLines(sale.id, payload)
      setSale(result.sale)
      return result.sale
    } catch (err) {
      setRedeemError(packageRedeemErrorMessage(
        err,
        'Unable to add package redemptions to ticket.',
      ))
      return null
    } finally {
      setMutating(false)
    }
  }, [sale])

  return {
    sale,
    stylists,
    paymentMethods,
    loadingPaymentMethods,
    addonOptionsByService,
    loading,
    mutating,
    error,
    customerError,
    paymentError,
    holdError,
    appointmentError,
    packageError,
    redeemError,
    discountError,
    floorQueueError,
    suspendedSales,
    loadingSuspended,
    branchLabel: activeBranch?.name ?? 'Branch',
    addService,
    addProduct,
    assignStylist,
    changeLineQuantity,
    attachAddon,
    detachAddon,
    voidLine,
    duplicateLine,
    updateLineDetails,
    updateSaleNotes,
    applyLineDiscountToSale,
    applyHeaderDiscountToSale,
    applyPromoCodeToSale,
    removePromoCodeFromSale,
    changeGenderPrice,
    assignCustomer,
    clearCustomer,
    paySale,
    startNewTicket,
    holdCurrentSale,
    recallSuspendedSale,
    loadSuspendedSales,
    loadAppointmentOntoTicket,
    sellPackage,
    redeemPackageService,
    redeemPackageServices,
    ensureAddonOptions,
    enqueueToFloorQueue,
    advanceLineStatus,
  }
}
